// pkg191 — Stay logged in defaults ON and remembers the last choice (D-S5); label no longer says 48 hours (admin web sessions are 30 days, pkg190); a 'Your session expired' notice shows when the refresh path signed the user out — delivered 2026-10-31
// pkg134 — Terms + Privacy links under the sign-in card (pre-auth access; public pkg84 routes) — delivered 2026-07-27
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg20b — /admin redirects to the dashboard when a session is already active (silent refresh restore was invisible from this page) — delivered 2026-06-11
// pkg20a — master admin password login removed; email + password is the only admin sign-in — delivered 2026-06-10
import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useFirmName } from '../contexts/FirmSettingsContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { LS_REMEMBER_PREF, SS_EXPIRED_FLAG } from '../utils/adminSession'; // pkg191

const REMEMBER_EMAIL_KEY = 'ryanlaw_admin_remembered_email';

// Sign-in is email + password only — validates against admin_users.
// (The legacy master admin password option was removed in pkg20a.)
//
// pkg20b: this page is the natural admin entry point (/admin), so it must
// acknowledge an already-restored session. On startup, AdminAuthContext
// silently exchanges a stored refresh token ("Stay logged in") for a fresh
// access token — but this page previously rendered the login form
// unconditionally, hiding the restored session and forcing a re-login.
// Now: while the context is resolving, show a spinner (same treatment as
// RequireAdmin); once resolved, an active session redirects straight to the
// dashboard; only a genuinely signed-out visitor sees the form.
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const firmName = useFirmName();
  const { adminUser, loading: authLoading, loginWithCredentials } = useAdminAuth();


  // Seed email from localStorage if previously remembered.
  const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  const [email,          setEmail]          = useState(savedEmail);
  const [password,       setPassword]       = useState('');
  // pkg191 (D-S5): default ON; a prior explicit choice wins.
  const [rememberMe,     setRememberMe]      = useState(() => localStorage.getItem(LS_REMEMBER_PREF) !== '0');
  const [rememberEmail,  setRememberEmail]   = useState(!!savedEmail);
  // pkg191: set by utils/adminSession when a refresh token was rejected.
  const [expiredNotice] = useState(() => {
    const flagged = sessionStorage.getItem(SS_EXPIRED_FLAG) === '1';
    if (flagged) sessionStorage.removeItem(SS_EXPIRED_FLAG);
    return flagged;
  });
  const [error,          setError]          = useState('');
  const [loading,        setLoading]        = useState(false);

  // Keep remembered email in sync when the checkbox changes.
  useEffect(() => {
    if (!rememberEmail) {
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  }, [rememberEmail]);

  // pkg20b — wait for the startup credential check before deciding what to
  // render. The silent refresh-token exchange is in flight during this window.
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ borderTopColor: '#0F1838', width: 24, height: 24 }} />
      </div>
    );
  }

  // pkg20b — session already active ("Stay logged in" restore, or navigating
  // back to /admin while signed in): go straight to the dashboard.
  if (adminUser) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  async function handleUserLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      // Persist (or clear) email based on checkbox.
      if (rememberEmail && email.trim()) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
      await loginWithCredentials(email.trim(), password, rememberMe);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  }


  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <h1>{firmName}</h1>
          <p>Attorney Administration</p>
        </div>

        {expiredNotice && !error && ( // pkg191
          <div className="alert" style={{ background: '#fff8e1', color: '#7a5a00', border: '1px solid #e8d48a' }}>
            Your session expired. Please sign in again.
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleUserLogin}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus={!savedEmail}
                required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus={!!savedEmail}
                required />
            </div>

            {/* Remember options */}
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={e => setRememberEmail(e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer' }}
                />
                Remember my email
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer' }}
                />
                Stay logged in on this browser
              </label>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? <><span className="spinner" />Signing in...</> : 'Sign In'}
            </button>
        </form>

        <p style={{ marginTop: 24, fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
          <a href="/" style={{ color: '#888' }}>Client Portal</a>
        </p>
        {/* pkg134 — pre-auth legal links (parity with mobile pkg131: the
            policy must be reachable before sign-in too) */}
        <p style={{ marginTop: 12, fontSize: 11, color: '#aaa', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>Terms of Service</a>
          <span style={{ margin: '0 6px' }}>·</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
